import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import Layout from "../Landing/Layout";

const EMAILJS_PUBLIC_KEY =
  process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "-PeFNIlQtyA7tkVIX";
const EMAILJS_SERVICE_ID =
  process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_z5wxydv";
const EMAILJS_TEMPLATE_ID =
  process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_40n6mx9";
const INDIA_PHONE_PREFIX = "+91";

const formatIndiaPhoneForEmail = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (raw.startsWith("+")) return raw;
  return `${INDIA_PHONE_PREFIX} ${raw}`;
};

const Contact = () => {
  const [toast, setToast] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });

  const showToast = (title, description) => {
    setToast({ title, description });
    window.setTimeout(() => setToast(null), 3500);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          name: `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim(),
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formatIndiaPhoneForEmail(formData.phone),
          message: formData.message.trim(),
          source: "Contact page form",
          page_url: typeof window !== "undefined" ? window.location.href : "",
        },
        { publicKey: EMAILJS_PUBLIC_KEY }
      );
      showToast("Message Sent", "Thank you for your message. We will get back to you soon.");
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
      });
    } catch (error) {
      console.error("[Contact] EmailJS failed:", error);
      showToast("Error", "Something went wrong. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  return (
    <Layout>
      <section className="page-hero">
        <div className="section-container animate-fade-in-up">
          <div className="grid-12 items-start">
            <div className="lg:col-span-5">
              <h1 className="heading-display mb-6">Start a Conversation</h1>

              <p className="body-text text-muted-foreground text-measure mb-4">
                Douglas & McCartney welcomes conversations with real estate professionals exploring a structured, long-term approach to advisory practice.
              </p>

              <p className="body-text text-muted-foreground text-measure mb-8">
                Whether you are an experienced broker or an advisor seeking platform support, we invite you to begin a discussion.
              </p>

              <a href="tel:+919535298949" className="btn-primary inline-block">
                Request a Discussion
              </a>
            </div>

            <div className="content-card lg:col-span-7">
              <h2 className="heading-subsection text-muted-foreground mb-6">Contact us</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="body-small block mb-2">First name *</label>
                    <input
                      id="firstName"
                      name="firstName"
                      placeholder="First name"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="landing-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="body-small block mb-2">Last name *</label>
                    <input
                      id="lastName"
                      name="lastName"
                      placeholder="Last name"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="landing-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phone" className="body-small block mb-2">Phone *</label>
                    <div className="flex gap-2 contact-phone-row">
                      <div className="flex items-center gap-1 px-3 bg-card border border-border rounded-sm contact-phone-prefix">
                        <span className="text-lg">+91</span>
                      </div>
                      <input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="Phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="landing-input flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="email" className="body-small block mb-2">Email</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Email"
                      value={formData.email}
                      onChange={handleChange}
                      className="landing-input"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="body-small block mb-2">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    placeholder="Message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="landing-textarea"
                  />
                </div>

                <button type="submit" className="btn-primary w-full" disabled={isSubmitting}>
                  {isSubmitting ? "Sending..." : "Submit"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {toast && (
        <div className="landing-toast" role="status" aria-live="polite">
          <p className="label-text text-muted-foreground">{toast.title}</p>
          <p className="body-small text-muted-foreground">{toast.description}</p>
        </div>
      )}
    </Layout>
  );
};

export default Contact;
